import { t } from '@lingui/core/macro';
import type { BookingProposalRecord } from '@navio/nsdk';
import { PaymentOption } from '@navio/nsdk';
import { commonTrackingEvents, tracking } from 'noddi-web';
import { ConfirmStationaryBookingButton } from '../../components/BookingFlow/Stationary/ConfirmStationaryBookingButton';
import { useCurrentBrand } from '../../stores/BrandStore/brandStore';
import {
  useBookingProposalActions,
  useBookingProposalAnalytics,
  useBookingProposalStationaryReservation
} from '../../stores/bookingProposalStore';
import { buildStationaryCreateBody } from '../../utils/stationaryConfirm';

type Props = {
  proposal: BookingProposalRecord;
  paymentOption: PaymentOption;
  userGroupPayerId?: number | null;
  hasValidPhone?: boolean;
  hasOrganizationNumber?: boolean;
  /** Proposal UI total — same source as the summary card. */
  totalAmount: number;
  /** Single owner from checkout hook — do not re-resolve org UG here. */
  bookingUserGroupId: number | null;
  /** From parent useCancelHoldOnExpiry — do not cancel holds here. */
  isReservationExpired?: boolean;
};

export const ConfirmStationaryProposalButton = ({
  proposal,
  paymentOption,
  userGroupPayerId,
  hasValidPhone = true,
  hasOrganizationNumber = true,
  totalAmount,
  bookingUserGroupId,
  isReservationExpired = false
}: Props) => {
  const { currentBrand } = useCurrentBrand();
  const stationaryReservation = useBookingProposalStationaryReservation();
  const { clearStore } = useBookingProposalActions();
  const analytics = useBookingProposalAnalytics();

  const primaryItem = proposal.bookingProposalItems[0];
  const car = primaryItem?.car;
  const salesItems = primaryItem?.salesItemsWithQuantity ?? [];
  const primarySalesItem = salesItems.find((si) => !si.salesItem.isAddon);
  const addonSalesItems = salesItems.filter((si) => si.salesItem.isAddon);

  return (
    <ConfirmStationaryBookingButton
      paymentOption={paymentOption}
      totalAmount={totalAmount}
      hasValidPhone={hasValidPhone}
      hasOrganizationNumber={hasOrganizationNumber}
      isReservationExpired={isReservationExpired}
      errorMode='inline'
      allowLoginPrompt
      onBookingCreated={({ bookingId, paymentMethod }) => {
        tracking.track(commonTrackingEvents.proposalAccepted, { ...analytics, bookingId });
        // Pay-before still needs the reservation in the store until payment
        // navigation finishes — clearing early remounts the proposal landing.
        if (paymentMethod !== PaymentOption.PAY_BEFORE) {
          clearStore();
        }
      }}
      buildCreateBody={() => {
        if (!primaryItem || !primarySalesItem || !car) {
          return { ok: false, message: t`No primary service found for this proposal. Please contact support.` };
        }

        return buildStationaryCreateBody({
          licensePlate: car.licensePlate,
          hold: stationaryReservation,
          salesItemIds: [primarySalesItem.salesItem.id, ...addonSalesItems.map((si) => si.salesItem.id)],
          userId: proposal.userGroupMember.user.id,
          userGroupId: bookingUserGroupId,
          brandDomain: currentBrand,
          bookingProposalId: proposal.id,
          userGroupPayerId,
          missingHoldMessage: t`No time slot selected. Please wait for available times to load.`
        });
      }}
    />
  );
};
